import type { Tela } from '@/features/auth/perfil-de-acesso';
import { apiRequest } from '@/shared/lib/api-client';

// ─────────── Users ───────────
export interface UserRow {
  id: number;
  name: string;
  email: string;
  profile: 'admin' | 'attendant';
  status: 'active' | 'inactive';
  /** true = usuário excluído (soft delete). Só aparece quando a listagem é
   *  pedida com include_deleted (filtro de Atendentes do Histórico de Chats). */
  deleted?: boolean;
  last_login_at: string | null;
  created_at: string;
  departments: Array<{ id: number; name: string; color: string }>;
  /** Ids dos grupos de acesso que o usuário é membro. */
  access_group_ids: number[];
  /** Ids dos canais que o usuário pode usar pra INICIAR atendimento. */
  whatsapp_number_ids: number[];
  /** Presença: conectado (socket) E não-marcado-offline. */
  online: boolean;
  /** URL (HMAC, estável) da foto de avatar — null se sem foto. */
  avatar_url: string | null;
  /** Gestor controla: se false, o atendente não pode desativar a assinatura. */
  allow_disable_signature: boolean;
  /** Limite de atendimentos ativos ao mesmo tempo. null = sem limite. */
  max_active_tickets: number | null;
  /** Perfil de acesso (só atendente; admin = null). null = só Atendimento. */
  access_profile_id: number | null;
  access_profile_name: string | null;
  /** "Visualizar tickets de outros usuários dos seus departamentos". */
  view_all_tickets: boolean;
  /** "Não receber tickets via distribuição automática". */
  skip_auto_assign: boolean;
}

export const listUsers = (opts?: { includeDeleted?: boolean }): Promise<UserRow[]> =>
  apiRequest<UserRow[]>(`/users${opts?.includeDeleted ? '?include_deleted=true' : ''}`);

/** Carga atual de cada atendente — nº de atendimentos ativos. Usado no modal
 *  de transferência pra distribuir por quem está menos ocupado. */
export interface AgentTicketCount {
  user_id: number;
  count: number;
}
export const listActiveTicketCounts = (): Promise<AgentTicketCount[]> =>
  apiRequest<AgentTicketCount[]>('/users/active-ticket-counts');
export const createUser = (input: {
  name: string;
  email: string;
  password: string;
  profile: 'admin' | 'attendant';
  department_ids?: number[];
  access_group_ids?: number[];
  whatsapp_number_ids?: number[];
  allow_disable_signature?: boolean;
  /** 0 = sem limite. */
  max_active_tickets?: number;
  /** Perfil de acesso (só vale pra attendant). null = só Atendimento. */
  access_profile_id?: number | null;
  view_all_tickets?: boolean;
  skip_auto_assign?: boolean;
}): Promise<{ id: number }> =>
  apiRequest<{ id: number }>('/users', { method: 'POST', body: input });
export const updateUser = (
  id: number,
  input: {
    name?: string;
    email?: string;
    profile?: 'admin' | 'attendant';
    department_ids?: number[];
    access_group_ids?: number[];
    whatsapp_number_ids?: number[];
    allow_disable_signature?: boolean;
    /** 0 = sem limite. */
    max_active_tickets?: number;
    access_profile_id?: number | null;
    view_all_tickets?: boolean;
    skip_auto_assign?: boolean;
  },
): Promise<void> => apiRequest<void>(`/users/${id}`, { method: 'PATCH', body: input });
export const resetUserPassword = (id: number, password: string): Promise<void> =>
  apiRequest<void>(`/users/${id}/reset-password`, { method: 'POST', body: { password } });
/** Admin: desloga o usuário de TODOS os dispositivos (revoga sessões). */
export const revokeUserSessions = (id: number): Promise<void> =>
  apiRequest<void>(`/users/${id}/revoke-sessions`, { method: 'POST', body: {} });
/** Admin: exclui (soft delete) o usuário — some da lista, libera o email,
 *  preserva o histórico. Bloqueia se houver atendimento aberto. */
export const deleteUser = (id: number): Promise<void> =>
  apiRequest<void>(`/users/${id}`, { method: 'DELETE' });

// ─────────── Departments ───────────
export interface DepartmentRow {
  id: number;
  tenantId?: number;
  name: string;
  color: string;
  createdAt?: string;
}
export const listDepartments = (): Promise<DepartmentRow[]> =>
  apiRequest<DepartmentRow[]>('/departments');
export const createDepartment = (input: { name: string; color?: string }): Promise<{
  id: number;
}> => apiRequest<{ id: number }>('/departments', { method: 'POST', body: input });
export const updateDepartment = (
  id: number,
  input: { name?: string; color?: string },
): Promise<void> => apiRequest<void>(`/departments/${id}`, { method: 'PATCH', body: input });
export const deleteDepartment = (id: number): Promise<void> =>
  apiRequest<void>(`/departments/${id}`, { method: 'DELETE' });

// ─────────── Tags ───────────
export interface TagRow {
  id: number;
  name: string;
  color: string;
  scope: 'ticket' | 'contact' | 'both';
}
export const createTag = (
  input: TagRow extends infer _ ? Omit<TagRow, 'id'> : never,
): Promise<{
  id: number;
}> => apiRequest<{ id: number }>('/tags', { method: 'POST', body: input });
export const updateTag = (id: number, input: Partial<Omit<TagRow, 'id'>>): Promise<void> =>
  apiRequest<void>(`/tags/${id}`, { method: 'PATCH', body: input });
export const deleteTag = (id: number): Promise<void> =>
  apiRequest<void>(`/tags/${id}`, { method: 'DELETE' });

// ─────────── Closing Reasons ───────────
export interface ClosingReasonRow {
  id: number;
  name: string;
  active: boolean;
  closingMessage: string | null;
  sendRating: boolean;
  /** Motivo do sistema ("Não especificado"): não aparece nesta tela. */
  systemKey?: string | null;
}
export const createClosingReason = (input: {
  name: string;
  active?: boolean;
  closingMessage?: string | null;
  sendRating?: boolean;
}): Promise<{ id: number }> =>
  apiRequest<{ id: number }>('/closing-reasons', { method: 'POST', body: input });
export const updateClosingReason = (
  id: number,
  input: { name?: string; active?: boolean; closingMessage?: string | null; sendRating?: boolean },
): Promise<void> => apiRequest<void>(`/closing-reasons/${id}`, { method: 'PATCH', body: input });
export const deleteClosingReason = (id: number): Promise<void> =>
  apiRequest<void>(`/closing-reasons/${id}`, { method: 'DELETE' });

// ─────────── Holidays (feriados) ───────────
export interface HolidayRow {
  id: number;
  name: string;
  /** Data única 'YYYY-MM-DD' (Drizzle serializa a coluna `holiday_date`). */
  holidayDate: string;
  message: string;
}
export const listHolidays = (): Promise<HolidayRow[]> => apiRequest<HolidayRow[]>('/holidays');
export const createHoliday = (input: {
  name: string;
  date: string;
  message: string;
}): Promise<{ id: number }> =>
  apiRequest<{ id: number }>('/holidays', { method: 'POST', body: input });
export const updateHoliday = (
  id: number,
  input: { name?: string; date?: string; message?: string },
): Promise<void> => apiRequest<void>(`/holidays/${id}`, { method: 'PATCH', body: input });
export const deleteHoliday = (id: number): Promise<void> =>
  apiRequest<void>(`/holidays/${id}`, { method: 'DELETE' });

// ─────────── Quick Replies ───────────
export interface QuickReplyRow {
  id: number;
  shortcut: string;
  body: string;
  /** null = da empresa (editável, com grupos); objeto = pessoal do atendente
   *  (admin vê/edita/exclui p/ moderação, sem grupos). Migration 0080. */
  created_by: { id: number; name: string } | null;
  /** Grupos que podem ver a mensagem. Vazio = todos veem. Só se aplica às da
   *  empresa (pessoal é privada, sem grupo). */
  access_group_ids: number[];
}
/** Lista de GESTÃO (todas, com grupos) — não filtra por membership (≠ composer). */
export const listQuickRepliesManage = (): Promise<QuickReplyRow[]> =>
  apiRequest<QuickReplyRow[]>('/quick-replies/manage');
export const createQuickReply = (input: {
  shortcut: string;
  body: string;
  access_group_ids?: number[];
}): Promise<{
  id: number;
}> => apiRequest<{ id: number }>('/quick-replies', { method: 'POST', body: input });
export const updateQuickReply = (
  id: number,
  input: { shortcut?: string; body?: string; access_group_ids?: number[] },
): Promise<void> => apiRequest<void>(`/quick-replies/${id}`, { method: 'PATCH', body: input });
export const deleteQuickReply = (id: number): Promise<void> =>
  apiRequest<void>(`/quick-replies/${id}`, { method: 'DELETE' });

// ─────────── Access Groups (Grupos de Acesso) ───────────
export interface AccessGroupRow {
  id: number;
  name: string;
  description: string | null;
  memberCount: number;
}
export const listAccessGroups = (): Promise<AccessGroupRow[]> =>
  apiRequest<AccessGroupRow[]>('/access-groups');
export const createAccessGroup = (input: {
  name: string;
  description?: string;
}): Promise<{ id: number }> =>
  apiRequest<{ id: number }>('/access-groups', { method: 'POST', body: input });
export const updateAccessGroup = (
  id: number,
  input: { name?: string; description?: string },
): Promise<void> => apiRequest<void>(`/access-groups/${id}`, { method: 'PATCH', body: input });
export const deleteAccessGroup = (id: number): Promise<void> =>
  apiRequest<void>(`/access-groups/${id}`, { method: 'DELETE' });
export const getAccessGroupMembers = (id: number): Promise<{ user_ids: number[] }> =>
  apiRequest<{ user_ids: number[] }>(`/access-groups/${id}/members`);
export const setAccessGroupMembers = (id: number, userIds: number[]): Promise<void> =>
  apiRequest<void>(`/access-groups/${id}/members`, {
    method: 'PUT',
    body: { user_ids: userIds },
  });

// ─────────── Perfis de Acesso ───────────
export interface AccessProfileRow {
  id: number;
  name: string;
  screens: Tela[];
  /** Usuários (vivos) com este perfil. */
  user_count: number;
}
export interface AccessProfileInput {
  name: string;
  screens: Tela[];
}
export const listAccessProfiles = (): Promise<AccessProfileRow[]> =>
  apiRequest<AccessProfileRow[]>('/access-profiles');
export const createAccessProfile = (input: AccessProfileInput): Promise<{ id: number }> =>
  apiRequest<{ id: number }>('/access-profiles', { method: 'POST', body: input });
export const updateAccessProfile = (
  id: number,
  input: Partial<AccessProfileInput>,
): Promise<void> => apiRequest<void>(`/access-profiles/${id}`, { method: 'PATCH', body: input });
export const deleteAccessProfile = (id: number): Promise<void> =>
  apiRequest<void>(`/access-profiles/${id}`, { method: 'DELETE' });

// ─────────── Templates ───────────
export interface TemplateRow {
  id: number;
  whatsappAccountId: number;
  metaTemplateId: string | null;
  name: string;
  language: string;
  category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
  status: 'pending' | 'approved' | 'rejected' | 'disabled' | 'paused';
  components: unknown[];
  variableCount: number;
  qualityScore: string | null;
  rejectedReason: string | null;
  lastSyncedAt: string | null;
  channelNumbers: string[];
}

export const listTemplates = (
  status?: string,
  whatsappNumberId?: number,
): Promise<TemplateRow[]> => {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (whatsappNumberId) params.set('whatsapp_number_id', String(whatsappNumberId));
  const qs = params.toString();
  return apiRequest<TemplateRow[]>(`/templates${qs ? `?${qs}` : ''}`);
};
export const syncTemplates = (): Promise<{
  synced_accounts: number;
  upserted_templates: number;
  errors: Array<{ wabaId: string; error: string }>;
}> => apiRequest('/templates/sync', { method: 'POST', body: {} });

/** Oculta o template da lista do painel (soft-delete só-local; `Sincronizar`
 *  traz de volta). NÃO apaga na Meta. */
export const deleteTemplate = (id: number): Promise<{ ok: true }> =>
  apiRequest(`/templates/${id}`, { method: 'DELETE' });

// ─────────── Settings KV ───────────
export interface AdvancedSetting {
  key: string;
  value: unknown;
}
export const getSetting = (key: string): Promise<{ key: string; value: unknown }> =>
  apiRequest(`/settings/${key}`);
export const listSettings = (prefix: string): Promise<Array<{ key: string; value: unknown }>> =>
  apiRequest(`/settings?prefix=${encodeURIComponent(prefix)}`);
export const putSetting = (key: string, value: unknown): Promise<void> =>
  apiRequest<void>(`/settings/${key}`, { method: 'PUT', body: { value } });

// ─────────── Custom Field Definitions ───────────
export interface CustomFieldDef {
  id: number;
  key: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'boolean' | 'enum';
  options: string[];
  required: boolean;
  position: number;
}
export const listCustomFieldDefs = (): Promise<CustomFieldDef[]> =>
  apiRequest('/custom-field-definitions');
export const createCustomFieldDef = (input: Omit<CustomFieldDef, 'id'>): Promise<{ id: number }> =>
  apiRequest<{ id: number }>('/custom-field-definitions', { method: 'POST', body: input });
export const updateCustomFieldDef = (
  id: number,
  input: Partial<Omit<CustomFieldDef, 'id' | 'key'>>,
): Promise<void> =>
  apiRequest<void>(`/custom-field-definitions/${id}`, { method: 'PATCH', body: input });
export const deleteCustomFieldDef = (id: number): Promise<void> =>
  apiRequest<void>(`/custom-field-definitions/${id}`, { method: 'DELETE' });

// ─────────── LGPD ───────────
export interface LgpdRequestRow {
  id: number;
  request_type: 'export' | 'delete' | 'anonymize';
  status: 'pending' | 'processing' | 'completed' | 'failed';
  contact_id: number | null;
  contact_snapshot: {
    display_name?: string | null;
    phone?: string | null;
    email?: string | null;
  };
  requested_by_user_id: number | null;
  error_message: string | null;
  has_result: boolean;
  result_size_bytes: number | null;
  completed_at: string | null;
  created_at: string;
}
export const listLgpdRequests = (): Promise<LgpdRequestRow[]> =>
  apiRequest<LgpdRequestRow[]>('/lgpd/requests');
export const createLgpdRequest = (input: {
  contact_id: number;
  request_type: 'export' | 'delete' | 'anonymize';
}): Promise<{ id: number }> =>
  apiRequest<{ id: number }>('/lgpd/requests', { method: 'POST', body: input });
export const downloadLgpdResult = (id: number): Promise<{ url: string }> =>
  apiRequest<{ url: string }>(`/lgpd/requests/${id}/download`);
export const retryLgpdRequest = (id: number): Promise<void> =>
  apiRequest<void>(`/lgpd/requests/${id}/retry`, { method: 'POST', body: {} });

// ─────────── API Keys (automação externa / n8n) ───────────
export interface ApiKeyRow {
  id: number;
  name: string;
  token_prefix: string;
  /** true = token guardado cifrado (dá pra copiar via /reveal). Chaves antigas
   *  só têm o hash → false → só recriar. */
  copyable: boolean;
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
}
export const listApiKeys = (): Promise<{ items: ApiKeyRow[] }> =>
  apiRequest<{ items: ApiKeyRow[] }>('/api-keys');
export const createApiKey = (
  name: string,
): Promise<{ id: number; name: string; token: string; token_prefix: string }> =>
  apiRequest('/api-keys', { method: 'POST', body: { name } });
export const revealApiKey = (id: number): Promise<{ token: string }> =>
  apiRequest<{ token: string }>(`/api-keys/${id}/reveal`);
export const deleteApiKey = (id: number): Promise<void> =>
  apiRequest<void>(`/api-keys/${id}`, { method: 'DELETE' });
